import "./index.css";

import { Button } from "@tql/ui-components-react/tql/buttons";
import { plusCircleLine } from "@tql/ui-components-react/untitled/icons";
import { useState } from "react";

import { useGetInternalUserPreferences } from "~/features/profile/hooks/internal/use-get-internal-user-preferences.ts";
import { useInternalGridPreferencesSync } from "~/features/profile/hooks/internal/use-internal-grid-preferences-sync.ts";
import { useGetUserPreferences } from "~/features/profile/hooks/use-get-user-preferences.ts";
import { useGridPreferencesSync } from "~/features/profile/hooks/use-grid-preferences-sync.ts";
import { NewCommoditiesModal } from "~/shared/components/commodities/new-commodities-modal.tsx";
import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { TraxPageLayout } from "~/shared/components/trax-page-layout/trax-page-layout.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { getDefaultCommoditiesGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { CommoditiesGrid } from "./components/commodities-grid/commodities-grid.tsx";
import { CommoditiesHeader } from "./components/commodities-header/commodities-header.tsx";
import { CommoditiesMobile } from "./components/commodities-mobile/commodities-mobile.tsx";
import { INITIAL_COMMODITY_COLUMNS } from "./constants/column-constants.ts";
import { useCommoditiesGridColumns } from "./hooks/use-commodities-grid-columns.ts";
import { useCommoditiesNavigation } from "./hooks/use-commodities-navigation.ts";

export const Commodities = () => {
	const { isInternal } = useDeploymentType();
	const { isEnabled: isMobileViewEnabled } = useFeatureFlagGate(
		// The LD React SDK camelCases flag keys by default (useCamelCaseFlagKeys),
		// so the kebab-case LaunchDarkly key must be referenced here in its
		// transformed form or it never matches.
		"enableTraxCommoditiesMobileView",
	);
	const isMobile = useIsMobile() && isMobileViewEnabled;
	const { activeCustomer, handleSelectCustomer, isCustomerLoading } =
		useCommoditiesNavigation();

	const [showNewCommoditiesModal, setShowNewCommoditiesModal] = useState(false);

	// Internal and external users both use the server-backed preference path
	// (visibility, order, width) via independent endpoints/hooks. The active set
	// is selected by deployment type; the external flow is left untouched.
	const { data: externalCommodityPreferences } = useGetUserPreferences(
		GridIdentifiers.Commodities,
	);
	const { data: internalCommodityPreferences } = useGetInternalUserPreferences(
		GridIdentifiers.Commodities,
	);
	const commodityPreferences = isInternal
		? internalCommodityPreferences
		: externalCommodityPreferences;

	const selectedColumns = useCommoditiesGridColumns({
		baseColumns: INITIAL_COMMODITY_COLUMNS,
		isInternal,
		columnPrefs: commodityPreferences?.columns,
	});

	const gridPrefsOptions = {
		defaultPrefs:
			getDefaultCommoditiesGridPrefs(isInternal)[GridIdentifiers.Commodities]
				?.columns,
	};
	const externalGridPrefs = useGridPreferencesSync(
		GridIdentifiers.Commodities,
		gridPrefsOptions,
	);
	const internalGridPrefs = useInternalGridPreferencesSync(
		GridIdentifiers.Commodities,
		gridPrefsOptions,
	);
	const {
		gridKey: prefsGridKey,
		onColumnVisibilityChange: handleToggleColumn,
		onColumnReorder: handleColumnReorder,
		onColumnResize: handleColumnResize,
		resetColumns: handleResetColumns,
	} = isInternal ? internalGridPrefs : externalGridPrefs;

	return (
		<TraxPageLayout>
			<TraxPageHeader
				isLtlOnly={!isInternal}
				representativeType={isInternal ? undefined : "ltl"}
				showSupportMessage={!isInternal}
				title={"Commodities"}
			/>
			<div
				className={`commodities-content ${isMobile ? "commodities-content-mobile" : ""}`}
			>
				<CommoditiesHeader
					columns={selectedColumns}
					isMobile={isMobile}
					onColumnToggle={handleToggleColumn}
					onCustomerChange={(customer) => {
						handleSelectCustomer(customer ?? undefined);
					}}
					onResetColumns={handleResetColumns}
					selectedCustomer={activeCustomer}
				/>
				{isCustomerLoading ? (
					<LoadingSpinner />
				) : (
					activeCustomer && (
						<div className={"commodities-grid-section"}>
							<div className={"commodities-grid-section-fill-height"}>
								{isMobile ? (
									<CommoditiesMobile customerId={String(activeCustomer.id)} />
								) : (
									<div className={"commodities-grid-wrapper"}>
										<CommoditiesGrid
											className={"commodities-grid"}
											customerId={String(activeCustomer.id)}
											onColumnReorder={handleColumnReorder}
											onColumnResize={handleColumnResize}
											prefsGridKey={prefsGridKey}
											style={{ height: "100%" }}
											visibleColumns={selectedColumns}
										/>
									</div>
								)}

								<div className={"add-item-button-container"}>
									<Button
										onClick={() => setShowNewCommoditiesModal(true)}
										svgIcon={plusCircleLine}
										variant={"primary"}
									>
										Add Item
									</Button>
								</div>
							</div>
						</div>
					)
				)}
			</div>
			{showNewCommoditiesModal ? (
				<NewCommoditiesModal
					customerId={activeCustomer?.id}
					onClose={() => setShowNewCommoditiesModal(false)}
				/>
			) : null}
		</TraxPageLayout>
	);
};
